import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  BookOpen, Plus, Pencil, Trash2, Loader2, Eye, EyeOff, 
  User, Crown, Calendar, ChevronDown, ChevronUp, Image
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";

const HistoryAdmin = () => {
  const { token } = useAuth();
  const [historyYears, setHistoryYears] = useState([]);
  const [stories, setStories] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState("years");
  const [editingYear, setEditingYear] = useState(null);
  const [editingStory, setEditingStory] = useState(null);
  const [editingManager, setEditingManager] = useState(null);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [expandedStory, setExpandedStory] = useState(null);

  const emptyYear = {
    year: new Date().getFullYear(),
    content: "",
    highlights: [],
    image_url: ""
  };

  const emptyManager = {
    name: "",
    years: "",
    matches: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    win_percentage: 0,
    biography: "",
    image_url: ""
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [yearsRes, storiesRes, managersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/history`),
        axios.get(`${API}/admin/stories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/managers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/section-toggles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setHistoryYears(yearsRes.data.sort((a, b) => b.year - a.year));
      setStories(storiesRes.data.stories || storiesRes.data || []);
      setManagers(managersRes.data.managers || managersRes.data || []);
      setSectionEnabled(settingsRes.data.history_section_enabled);
    } catch (error) {
      console.error("Failed to fetch history data", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API}/admin/section-toggle/history_section_enabled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.history_section_enabled);
      toast.success(`History section ${response.data.history_section_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle section");
    } finally {
      setToggling(false);
    }
  };

  // History Year CRUD
  const saveYear = async () => {
    try {
      const payload = {
        ...editingYear,
        highlights: Array.isArray(editingYear.highlights) 
          ? editingYear.highlights 
          : editingYear.highlights?.split('\n').filter(h => h.trim()) || []
      };
      
      if (editingYear.id) {
        await axios.put(`${API}/history/${editingYear.year}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Year updated");
      } else {
        await axios.post(`${API}/history`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Year created");
      }
      setYearDialogOpen(false);
      setEditingYear(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save year");
    }
  };

  const deleteYear = async (year) => {
    if (!confirm(`Are you sure you want to delete history for ${year}?`)) return;
    try {
      await axios.delete(`${API}/history/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Year deleted");
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete year");
    }
  };

  // Story CRUD
  const toggleStoryVisibility = async (storyId) => {
    try {
      await axios.post(`${API}/admin/stories/${storyId}/toggle-visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Story visibility updated");
      fetchAll();
    } catch (error) {
      toast.error("Failed to update story");
    }
  };

  const saveStory = async () => {
    try {
      await axios.put(`${API}/admin/stories/${editingStory.id}`, editingStory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Story updated");
      setStoryDialogOpen(false);
      setEditingStory(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save story");
    }
  };

  // Manager CRUD
  const saveManager = async () => {
    try {
      await axios.put(`${API}/admin/managers/${editingManager.id}`, editingManager, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Manager updated");
      setManagerDialogOpen(false);
      setEditingManager(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save manager");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="history-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <BookOpen className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          HISTORY ADMIN
        </h1>
      </div>

      {/* Section Visibility Toggle */}
      <div className={`rounded-xl p-4 mb-6 border-2 ${sectionEnabled ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sectionEnabled ? <Eye className="w-6 h-6 text-green-600" /> : <EyeOff className="w-6 h-6 text-red-600" />}
            <div>
              <h3 className="font-bold text-slate-800">History Section Visibility</h3>
              <p className="text-sm text-slate-600">
                {sectionEnabled ? "Section is VISIBLE on the website" : "Section is HIDDEN from the website"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${sectionEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {sectionEnabled ? "VISIBLE" : "HIDDEN"}
            </span>
            <Switch
              checked={sectionEnabled}
              onCheckedChange={toggleSection}
              disabled={toggling}
              data-testid="toggle-history-visibility"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="years" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Years ({historyYears.length})
          </TabsTrigger>
          <TabsTrigger value="stories" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Stories ({stories.length})
          </TabsTrigger>
          <TabsTrigger value="managers" className="flex items-center gap-2">
            <Crown className="w-4 h-4" /> Managers ({managers.length})
          </TabsTrigger>
        </TabsList>

        {/* Years Tab */}
        <TabsContent value="years">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => { setEditingYear({...emptyYear}); setYearDialogOpen(true); }}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-year-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Year
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[400px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="w-24">Year</TableHead>
                    <TableHead>Content Preview</TableHead>
                    <TableHead className="w-32">Highlights</TableHead>
                    <TableHead className="w-20">Image</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyYears.map(year => (
                    <TableRow key={year.id || year.year}>
                      <TableCell className="font-bold text-lg">{year.year}</TableCell>
                      <TableCell>
                        <div className="max-w-md truncate text-sm text-slate-600">
                          {year.content?.substring(0, 100)}...
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{year.highlights?.length || 0} items</TableCell>
                      <TableCell>
                        {year.image_url ? (
                          <Image className="w-4 h-4 text-green-600" />
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-7 p-0"
                            onClick={() => { 
                              setEditingYear({
                                ...year, 
                                highlights: year.highlights?.join('\n') || ''
                              }); 
                              setYearDialogOpen(true); 
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            onClick={() => deleteYear(year.year)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Stories Tab */}
        <TabsContent value="stories">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-24">Category</TableHead>
                    <TableHead className="w-24">Year</TableHead>
                    <TableHead className="w-24">Visible</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stories.map(story => (
                    <TableRow key={story.id} className={story.is_visible === false ? "bg-slate-50 opacity-60" : ""}>
                      <TableCell>
                        <div className="font-medium">{story.title}</div>
                        <div className="text-xs text-slate-500 max-w-md truncate">
                          {story.content?.substring(0, 80)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 capitalize">
                          {story.category?.replace('_', ' ') || 'General'}
                        </span>
                      </TableCell>
                      <TableCell>{story.year_from || '-'}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`h-7 px-2 ${story.is_visible !== false ? "text-green-600" : "text-slate-400"}`}
                          onClick={() => toggleStoryVisibility(story.id)}
                        >
                          {story.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => { setEditingStory({...story}); setStoryDialogOpen(true); }}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Managers Tab */}
        <TabsContent value="managers">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="w-16">Photo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-28">Years</TableHead>
                    <TableHead className="w-20">Matches</TableHead>
                    <TableHead className="w-20">Win %</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {managers.map(manager => (
                    <TableRow key={manager.id}>
                      <TableCell>
                        {manager.image_url ? (
                          <img 
                            src={manager.image_url} 
                            alt={manager.name} 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{manager.name}</TableCell>
                      <TableCell className="text-sm">{manager.years}</TableCell>
                      <TableCell>{manager.matches}</TableCell>
                      <TableCell>
                        <span className={`font-bold ${manager.win_percentage > 40 ? "text-green-600" : manager.win_percentage > 30 ? "text-amber-600" : "text-red-600"}`}>
                          {manager.win_percentage?.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => { setEditingManager({...manager}); setManagerDialogOpen(true); }}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Year Dialog */}
      <Dialog open={yearDialogOpen} onOpenChange={setYearDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingYear?.id ? `EDIT ${editingYear.year}` : "ADD NEW YEAR"}
            </DialogTitle>
          </DialogHeader>
          
          {editingYear && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year</Label>
                  <Input 
                    type="number"
                    value={editingYear.year} 
                    onChange={(e) => setEditingYear({...editingYear, year: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input 
                    value={editingYear.image_url || ""} 
                    onChange={(e) => setEditingYear({...editingYear, image_url: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Content (1000+ words)</Label>
                <Textarea 
                  value={editingYear.content} 
                  onChange={(e) => setEditingYear({...editingYear, content: e.target.value})}
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingYear.content?.split(/\s+/).filter(w => w).length || 0} words
                </p>
              </div>
              
              <div>
                <Label>Highlights (one per line)</Label>
                <Textarea 
                  value={editingYear.highlights} 
                  onChange={(e) => setEditingYear({...editingYear, highlights: e.target.value})}
                  rows={4}
                  placeholder="Key achievement 1&#10;Key achievement 2&#10;Key achievement 3"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setYearDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveYear} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-year-btn">
              {editingYear?.id ? "Update" : "Create"} Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Story Dialog */}
      <Dialog open={storyDialogOpen} onOpenChange={setStoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>EDIT STORY</DialogTitle>
          </DialogHeader>
          
          {editingStory && (
            <div className="grid gap-4 py-4">
              <div>
                <Label>Title</Label>
                <Input 
                  value={editingStory.title} 
                  onChange={(e) => setEditingStory({...editingStory, title: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Category</Label>
                  <Input 
                    value={editingStory.category || ""} 
                    onChange={(e) => setEditingStory({...editingStory, category: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Year From</Label>
                  <Input 
                    type="number"
                    value={editingStory.year_from || ""} 
                    onChange={(e) => setEditingStory({...editingStory, year_from: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Year To</Label>
                  <Input 
                    type="number"
                    value={editingStory.year_to || ""} 
                    onChange={(e) => setEditingStory({...editingStory, year_to: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Content</Label>
                <Textarea 
                  value={editingStory.content} 
                  onChange={(e) => setEditingStory({...editingStory, content: e.target.value})}
                  rows={8}
                />
              </div>
              
              <div>
                <Label>Image URL</Label>
                <Input 
                  value={editingStory.image_url || ""} 
                  onChange={(e) => setEditingStory({...editingStory, image_url: e.target.value})}
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingStory.is_visible !== false}
                  onCheckedChange={(v) => setEditingStory({...editingStory, is_visible: v})}
                />
                <Label className="mb-0">Visible on website</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveStory} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-story-btn">
              Update Story
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={managerDialogOpen} onOpenChange={setManagerDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>EDIT MANAGER</DialogTitle>
          </DialogHeader>
          
          {editingManager && (
            <div className="grid gap-4 py-4">
              <div className="flex justify-center">
                {editingManager.image_url ? (
                  <img 
                    src={editingManager.image_url} 
                    alt={editingManager.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-[#7C2C3B]"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-12 h-12 text-slate-400" />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input 
                    value={editingManager.name} 
                    onChange={(e) => setEditingManager({...editingManager, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Years</Label>
                  <Input 
                    value={editingManager.years} 
                    onChange={(e) => setEditingManager({...editingManager, years: e.target.value})}
                    placeholder="e.g. 2020-2024"
                  />
                </div>
              </div>
              
              <div>
                <Label>Image URL</Label>
                <Input 
                  value={editingManager.image_url || ""} 
                  onChange={(e) => setEditingManager({...editingManager, image_url: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label>Matches</Label>
                  <Input 
                    type="number"
                    value={editingManager.matches} 
                    onChange={(e) => setEditingManager({...editingManager, matches: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Wins</Label>
                  <Input 
                    type="number"
                    value={editingManager.wins} 
                    onChange={(e) => setEditingManager({...editingManager, wins: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Draws</Label>
                  <Input 
                    type="number"
                    value={editingManager.draws} 
                    onChange={(e) => setEditingManager({...editingManager, draws: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Losses</Label>
                  <Input 
                    type="number"
                    value={editingManager.losses} 
                    onChange={(e) => setEditingManager({...editingManager, losses: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Biography</Label>
                <Textarea 
                  value={editingManager.biography || ""} 
                  onChange={(e) => setEditingManager({...editingManager, biography: e.target.value})}
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setManagerDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveManager} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-manager-btn">
              Update Manager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoryAdmin;
